/**
 * citizenshipService.ts
 *
 * Frontend client for the Phase 2 / slice 2 citizenship endpoints
 * mounted at `/api/v2/citizenship` (see server/v2/routes/citizenship.js).
 *
 * Surface:
 *   - getResidencySnapshot(agent)            full snapshot (active country + row)
 *   - getMembership(agent, country)          single membership row
 *   - isActive(agent, country)               cheap boolean probe
 *
 *   - buildApplyTx({ agent, country, policyVersion })          unsigned XDR
 *   - buildRelinquishTx({ agent, country })                    unsigned XDR
 *
 *   - confirmCitizenship(...)                kicks off the x402 payment +
 *                                            admin-side confirm. The 402
 *                                            challenge is forwarded to the
 *                                            caller so the wallet can sign
 *                                            the payment payload.
 *
 *   - submitSignedAgentTx(signedXdr)         re-uses the existing
 *                                            /api/v2/agents/submit endpoint
 *                                            (via agentService.submitSignedAgentTx)
 *
 * Conventions match agentService.ts: backendBaseUrl resolution, asJson
 * error shape, BuiltTx { xdr, latestLedger } return type.
 */

import { submitSignedAgentTx, type SubmitResult } from './agentService';

// ── Smart-account submit helpers ────────────────────────────────────────────
//
// G-agents sign apply / relinquish themselves and submit through the
// standard /api/v2/agents/submit path (Stellar account is both tx source
// and signer).
//
// C-agents (passkey smart accounts) can't be tx sources. The backend
// builder uses the service account as source + expands the WebAuthn
// verifier footprint; the browser passkey-signs the auth entry only;
// submission must add the service-account envelope signature via
// /api/passkey-kit/fee-pay-and-submit (same pattern as DeFindex
// deposits and profile-set).
//
// Detection is purely by address shape — C... → smart account.

function isSmartAccount(address: string): boolean {
  return typeof address === 'string' && address.length > 0 && address[0] === 'C';
}

function passkeyBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const isDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return isDev
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : '';
}

async function feePayAndSubmit(signedXdr: string): Promise<SubmitResult> {
  const base = process.env.REACT_APP_BACKEND_URL || passkeyBaseUrl();
  const resp = await fetch(`${base}/api/passkey-kit/fee-pay-and-submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => resp.statusText);
    let msg = txt;
    try {
      const parsed = JSON.parse(txt);
      msg = parsed.error || parsed.message || txt;
    } catch { /* leave as-is */ }
    throw new Error(msg || `HTTP ${resp.status}`);
  }
  const data = await resp.json() as { success: true; hash: string; ledger?: number };
  return { hash: data.hash, ledger: data.ledger };
}

// ── Types ──────────────────────────────────────────────────────────────────

export type CitizenshipStatus =
  | 'pending'
  | 'active'
  | 'expired'
  | 'relinquished'
  | 'banned'
  | 'unknown';

export interface MembershipRow {
  agent: string | null;
  country: string | null;        // ISO-3166-1 alpha-2, uppercase
  status: number;                // 1..5
  statusName: CitizenshipStatus;
  appliedAtLedger: number;
  grantedAtLedger: number;
  expiresAtLedger: number;
  paidAmount: string;            // i128 stroops as string
  policyVersion: number;
  reason: string;                // 'none' until set
  version: number;
}

export interface ResidencySnapshot {
  configured: boolean;
  agent: string;
  country: string | null;
  active: MembershipRow | null;
}

export interface BuiltTx {
  xdr: string;
  latestLedger: number;
}

export interface ConfirmResult {
  hash: string;
  ledger?: number;
  payment: {
    verified: boolean;
    paidAmount: string | null;
    network: string | null;
    transaction: string | null;
  } | null;
}

export interface PaymentRequiredChallenge {
  status: 402;
  paymentRequired: unknown;       // raw x402 PAYMENT-REQUIRED header (base64 JSON)
  body: unknown;
}

// ── URL resolution ─────────────────────────────────────────────────────────

function backendBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const isDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return isDev
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : '';
}

function url(path: string): string {
  const base = process.env.REACT_APP_BACKEND_URL || backendBaseUrl();
  return `${base}/api/v2/citizenship${path}`;
}

async function asJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const txt = await resp.text().catch(() => resp.statusText);
    let msg = txt;
    try {
      const parsed = JSON.parse(txt);
      msg = parsed.error || parsed.message || txt;
    } catch { /* leave as-is */ }
    throw new Error(msg || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

// ── Reads ──────────────────────────────────────────────────────────────────

export async function getResidencySnapshot(agent: string): Promise<ResidencySnapshot> {
  if (!agent) throw new Error('agent is required');
  const resp = await fetch(url(`/${encodeURIComponent(agent)}`));
  return await asJson<ResidencySnapshot>(resp);
}

export async function getMembership(
  agent: string,
  country: string,
): Promise<MembershipRow | null> {
  if (!agent || !country) throw new Error('agent and country are required');
  const c = country.toUpperCase();
  const resp = await fetch(url(`/${encodeURIComponent(agent)}/${encodeURIComponent(c)}`));
  if (resp.status === 404) return null;
  const data = await asJson<{ success: true; membership: MembershipRow }>(resp);
  return data.membership;
}

export async function isActive(agent: string, country: string): Promise<boolean> {
  if (!agent || !country) return false;
  const c = country.toUpperCase();
  const resp = await fetch(url(`/${encodeURIComponent(agent)}/${encodeURIComponent(c)}/active`));
  const data = await asJson<{ active: boolean }>(resp);
  return Boolean(data.active);
}

// ── Tx builders ────────────────────────────────────────────────────────────

async function postBuild(path: string, body: unknown): Promise<BuiltTx> {
  const resp = await fetch(url(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await asJson<{ success: true; xdr: string; latestLedger: number }>(resp);
  return { xdr: data.xdr, latestLedger: data.latestLedger };
}

export function buildApplyTx(args: {
  agent: string;
  country: string;
  policyVersion: number;
}): Promise<BuiltTx> {
  const { agent, country, policyVersion } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/apply`, {
    country: country.toUpperCase(),
    policyVersion,
  });
}

export function buildRelinquishTx(args: { agent: string; country: string }): Promise<BuiltTx> {
  const { agent, country } = args;
  return postBuild(`/${encodeURIComponent(agent)}/build/relinquish`, {
    country: country.toUpperCase(),
  });
}

// Submission of the agent-signed XDR routes to whichever submit
// endpoint matches the agent's address shape:
//
//   - G-agent: /api/v2/agents/submit (also refreshes the local agent
//     index — harmless for citizenship).
//   - C-agent: /api/passkey-kit/fee-pay-and-submit (adds the service
//     account envelope signature; smart accounts can't sign envelopes
//     themselves).
//
// Callers must pass `agent` so we can detect the right path. The
// optional `agentToRefresh` only matters for G-agents and is ignored
// for C-agents (the agent registry index doesn't need refresh after
// a citizenship write).
export function submitSignedCitizenshipTx(
  signedXdr: string,
  opts: { agent: string; agentToRefresh?: string },
): Promise<SubmitResult> {
  if (isSmartAccount(opts.agent)) {
    return feePayAndSubmit(signedXdr);
  }
  return submitSignedAgentTx(signedXdr, { agentToRefresh: opts.agentToRefresh ?? opts.agent });
}

// ── Passkey-paid confirm (operator-driven, browser flow) ───────────────────
//
// Bypasses the x402 facilitator round-trip and instead orchestrates
// the USDC payment as a regular passkey-signed Soroban transfer:
//
//   1. POST /:agent/:country/build/payment  → unsigned XDR
//   2. caller passkey-signs the auth entry (passkeyKitService.signTransaction)
//   3. POST /:agent/:country/confirm/passkey → backend adds service-account
//      envelope sig, submits the transfer, then admin-invokes contract confirm
//
// Used by AgentTeleportModal's residency-change chain. The matching
// HTTP routes are defined in server/v2/routes/citizenship.js.

export interface PaymentBuildResult {
  unsignedXdr: string;
  latestLedger: number;
  payer: string;
  payTo: string;
  asset: string;
  amount: string;          // base units (stroops); 1.00 USDC → "10000000"
  amountHuman: string;     // e.g. "1.00"
  decimals: number;
  network: string;
}

export async function buildPaymentTx(args: {
  agent: string;
  country: string;
  payerAddress?: string;   // defaults to agent
}): Promise<PaymentBuildResult> {
  const { agent, country, payerAddress } = args;
  if (!agent || !country) throw new Error('agent and country are required');
  const resp = await fetch(
    url(`/${encodeURIComponent(agent)}/${encodeURIComponent(country.toUpperCase())}/build/payment`),
    {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payerAddress ? { payerAddress } : {}),
    },
  );
  const data = await asJson<{ success: true } & PaymentBuildResult>(resp);
  return {
    unsignedXdr:  data.unsignedXdr,
    latestLedger: data.latestLedger,
    payer:        data.payer,
    payTo:        data.payTo,
    asset:        data.asset,
    amount:       data.amount,
    amountHuman:  data.amountHuman,
    decimals:     data.decimals,
    network:      data.network,
  };
}

export async function confirmCitizenshipWithPasskey(args: {
  agent: string;
  country: string;
  signedXdr: string;
  expiresAtLedger: number;
}): Promise<ConfirmResult> {
  const { agent, country, signedXdr, expiresAtLedger } = args;
  if (!agent || !country) throw new Error('agent and country are required');
  if (!signedXdr) throw new Error('signedXdr is required');
  if (!Number.isFinite(expiresAtLedger) || expiresAtLedger <= 0) {
    throw new Error('expiresAtLedger must be > 0');
  }
  const resp = await fetch(
    url(`/${encodeURIComponent(agent)}/${encodeURIComponent(country.toUpperCase())}/confirm/passkey`),
    {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ signedXdr, expiresAtLedger }),
    },
  );
  return await asJson<ConfirmResult>(resp);
}

// ── Confirm (x402 + admin invocation) ───────────────────────────────────────

/**
 * Calls `POST /:agent/:country/confirm`.
 *
 * Behavior:
 *   - First call without a PAYMENT-SIGNATURE header → server replies
 *     402 with PAYMENT-REQUIRED. The browser layer is expected to:
 *       1. Parse the PAYMENT-REQUIRED header (base64 JSON payment
 *          requirements).
 *       2. Sign the Stellar payment with the user's wallet.
 *       3. Re-call this function with `paymentSignature` set.
 *   - Second call with a valid signature → backend verifies + settles
 *     via the facilitator and signs the on-chain `confirm` call.
 *
 * The x402 plumbing for the browser side already exists in the
 * border-proposals flow; this helper just exposes the endpoint.
 */
export async function confirmCitizenship(args: {
  agent: string;
  country: string;
  expiresAtLedger: number;
  paidAmount?: string;
  paymentSignature?: string;     // base64 PAYMENT-SIGNATURE payload
}): Promise<ConfirmResult | PaymentRequiredChallenge> {
  const { agent, country, expiresAtLedger, paidAmount, paymentSignature } = args;
  if (!agent || !country) throw new Error('agent and country are required');
  if (!Number.isFinite(expiresAtLedger) || expiresAtLedger <= 0) {
    throw new Error('expiresAtLedger must be > 0');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (paymentSignature) headers['PAYMENT-SIGNATURE'] = paymentSignature;

  const resp = await fetch(
    url(`/${encodeURIComponent(agent)}/${encodeURIComponent(country.toUpperCase())}/confirm`),
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        expiresAtLedger,
        ...(paidAmount ? { paidAmount } : {}),
      }),
    },
  );

  if (resp.status === 402) {
    let body: unknown = null;
    try { body = await resp.json(); } catch { /* may have no JSON body */ }
    return {
      status: 402,
      paymentRequired: resp.headers.get('PAYMENT-REQUIRED'),
      body,
    };
  }

  return await asJson<ConfirmResult>(resp);
}
